import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {BookingActionEnum, BookingButtonColorEnum, BookingTypeEnum} from '../enums/BookingEnums';
import {GtmEventEnum} from '../enums/GoogleEnums';
import {ModalTypeEnum} from '../enums/ModalEnums';
import {RoutesQueryParamsEnum} from '../enums/RoutesEnums';
import {BookingStepsEnum} from '../modules/booking-v2/enums/booking-enums';
import {BookingConfigurationData} from '../types/BookingConfigurationData';
import {BookingRouterState} from '../types/BookingRouterState';
import {DataLayerParamsClickGA4} from '../types/DataLayerParamsClickGA4';
import {ModalCommand} from '../types/ModalCommand';
import {GtmGa4EventPushService} from './gtm-ga4-event-push.service';
import {GtmService} from './gtm.service';
import {ModalsControlService} from './modals-control.service';
import {ToolsService} from './tools.service';
import {UrlBuilderService} from './url-builder.service';
import {BookingSettings} from '../types/BookingSettings';
import {CareproviderContactInfo} from '../types/CareproviderContactInfo';
import {BookingModalData} from '../types/BookingModalData';
import {DLPageTypeEnum} from '../enums/DataLayerEnums';

@Injectable()
export class BookingHelperService {
  private bookingRouterStateWriteable: BehaviorSubject<BookingRouterState> = new BehaviorSubject(null);
  public readonly bookingRouterState$: Observable<BookingRouterState> = this.bookingRouterStateWriteable.asObservable();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private modalControl: ModalsControlService,
    private tools: ToolsService,
    private gtmService: GtmService,
    private urlBuilder: UrlBuilderService,
    private router: Router,
    private gtmEventPush: GtmGa4EventPushService,
  ) {}

  public getBookingActionFromBookingConfiguration(bookingConfigurationData: BookingConfigurationData): string {
    let bookingAction = '';
    if (
      this.isBookingDefined(bookingConfigurationData) &&
      bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.VARDEN
    ) {
      bookingAction = BookingActionEnum.VARDEN_BOOKING;
    } else if (
      this.isBookingDefined(bookingConfigurationData) &&
      this.hasStaticUrlConfiguration(bookingConfigurationData) &&
      !this.hasCustomerFeatureConfiguration(bookingConfigurationData)
    ) {
      bookingAction = BookingActionEnum.URL;
    } else if (
      this.isBookingDefined(bookingConfigurationData) &&
      !this.hasStaticUrlConfiguration(bookingConfigurationData) &&
      !this.hasCustomerFeatureConfiguration(bookingConfigurationData)
    ) {
      bookingAction = BookingActionEnum.POPUP;
    } else if (this.hasCustomerFeatureConfiguration(bookingConfigurationData)) {
      bookingAction = BookingActionEnum.CUSTOM_POPUP;
    }
    return bookingAction;
  }

  public isBookingDefined(bookingConfigurationData: BookingConfigurationData): boolean {
    return (
      !this.tools.isNil(bookingConfigurationData) &&
      !this.tools.isNil(bookingConfigurationData.bookingSettings) &&
      !this.tools.isNil(bookingConfigurationData.bookingSettings.bookingType) &&
      bookingConfigurationData.bookingSettings.bookingType.id > 0
    );
  }

  public openBookingPopup(
    bookingConfigurationData: BookingConfigurationData,
    caregiverContactInfo: CareproviderContactInfo,
    pageType: DLPageTypeEnum,
  ): void {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: 'bokning',
      Action: 'visa förfrågan',
      Label: this.getEventText(bookingConfigurationData),
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);
    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `bokning - visa förfrågan`,
      cpruId: bookingConfigurationData.id,
      careServiceId: bookingConfigurationData.careserviceId,
      careServiceTypeId: bookingConfigurationData.careserviceTypeId,
      inferredCategoryTypeId: bookingConfigurationData.mainCaregiverUnitTypeId,
      inferredCareProviderTypeAreaId: bookingConfigurationData.areaId,
      mainCareProviderTypeId: bookingConfigurationData.mainCaregiverUnitTypeId,
      bookingType: bookingConfigurationData.bookingSettings.bookingType.name,
      pageType: pageType,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);

    const bookingModalData: BookingModalData = {
      bookingConfigurationData,
      caregiverContactInfo,
      pageType,
    };

    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.BOOKING,
      data: bookingModalData,
    };
    this.modalControl.openModal(modalCommand);
  }

  public openBookingCustomPopup(bookingConfigurationData: BookingConfigurationData, pageType: DLPageTypeEnum): void {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: 'bokning',
      Action: 'visa förfrågan',
      Label: this.getEventText(bookingConfigurationData),
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);
    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `bokning - visa förfrågan`,
      bookingType: bookingConfigurationData.bookingSettings.bookingType.name,
      cpruId: bookingConfigurationData.id,
      careServiceId: bookingConfigurationData.careserviceId,
      careServiceTypeId: bookingConfigurationData.careserviceTypeId,
      inferredCategoryTypeId: bookingConfigurationData.mainCaregiverUnitTypeId,
      inferredCareProviderTypeAreaId: bookingConfigurationData.areaId,
      mainCareProviderTypeId: bookingConfigurationData.mainCaregiverUnitTypeId,
      pageType: pageType,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);

    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.CUSTOM_BOOKING,
      data: bookingConfigurationData.customerFeature,
    };
    this.modalControl.openModal(modalCommand);
  }

  // public openAreYouListedPopup(
  //   bookingConfigurationData: BookingConfigurationData,
  //   caregiverContactInfo: CareproviderContactInfo,
  //   pageType: DLPageTypeEnum
  // ): void {
  //   const gtmTag = {
  //     event: GtmEventEnum.DEFAULT,
  //     Category: 'bokning',
  //     Action: 'är du redan listad här',
  //     Label: this.getEventText(bookingConfigurationData),
  //     Value: 1,
  //   };
  //   this.gtmService.pushTag(gtmTag);
  //
  //   const customGa4Params: DataLayerParamsClickGA4 = {
  //     callToAction: `bokning - är du redan listad här`,
  //     bookingType: bookingConfigurationData.bookingSettings.bookingType.name,
  //     cpruId: bookingConfigurationData.id,
  //     careServiceId: bookingConfigurationData.careserviceId,
  //     careServiceTypeId: bookingConfigurationData.careserviceTypeId,
  //     inferredCategoryTypeId: bookingConfigurationData.mainCaregiverUnitTypeId,
  //     inferredCareProviderTypeAreaId: bookingConfigurationData.areaId,
  //     mainCareProviderTypeId: bookingConfigurationData.mainCaregiverUnitTypeId,
  //     pageType: pageType,
  //   };
  //   const gtmGa4Tag = {
  //     event: GtmEventEnum.CTA_CLICK,
  //     ...customGa4Params,
  //   };
  //   this.gtmEventPush.pushClickEvent(gtmGa4Tag);
  //
  //   const bookingModalData: BookingModalData = {
  //     bookingConfigurationData,
  //     caregiverContactInfo,
  //     pageType,
  //   };
  //
  //   const modalCommand: ModalCommand = {
  //     type: ModalTypeEnum.ARE_YOU_LISTED,
  //     data: bookingModalData,
  //   };
  //   this.modalControl.openModal(modalCommand);
  // }

  public getBookingUrl(bookingConfigurationData: BookingConfigurationData): string {
    if (
      this.isBookingDefined(bookingConfigurationData) &&
      !this.tools.isNil(bookingConfigurationData.bookingSettings.bookingType) &&
      bookingConfigurationData.bookingSettings.bookingType.name !== BookingTypeEnum.VARDEN
    ) {
      return bookingConfigurationData.bookingSettings.bookingUrl;
    }
    return '';
  }

  public getBookingRouterLink(bookingConfigurationData: BookingConfigurationData): string[] {
    if (
      this.isBookingDefined(bookingConfigurationData) &&
      !this.tools.isNil(bookingConfigurationData.bookingSettings.bookingType) &&
      bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.VARDEN
    ) {
      return this.urlBuilder.CreateInternalBookingRouterLink(
        bookingConfigurationData.id,
        bookingConfigurationData.careserviceId,
      );
    }
    return [];
  }

  public getBookingLinkTarget(bookingConfigurationData: BookingConfigurationData): string {
    let bookingLinkTarget = '';
    if (
      this.isBookingDefined(bookingConfigurationData) &&
      !this.tools.isNil(bookingConfigurationData.bookingSettings.bookingType)
    ) {
      bookingLinkTarget =
        bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.VARDEN ? '_self' : '_blank';
    }
    return bookingLinkTarget;
  }

  public getBookingBtnLabel(bookingConfigurationData: BookingConfigurationData): string {
    let label = 'Boka nu';
    const bookingAction: string = this.getBookingActionFromBookingConfiguration(bookingConfigurationData);
    if (bookingAction === BookingActionEnum.CUSTOM_POPUP) {
      label = bookingConfigurationData.customerFeature.label;
    } else if (
      bookingAction === BookingActionEnum.POPUP &&
      (bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.TEL ||
        bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.EMAIL)
    ) {
      label = 'Ta kontakt';
    }

    return label;
  }

  public getBookingBtnColorV2(
    bookingConfigurationData: BookingConfigurationData,
  ): 'primary' | 'secondary' | 'tertiary' | 'green' | 'lightgreen' | 'apricot' | 'flat' | 'ghost' {
    if (
      !bookingConfigurationData ||
      !bookingConfigurationData.bookingSettings ||
      !bookingConfigurationData.bookingSettings.bookingType
    ) {
      return BookingButtonColorEnum.GREEN; //BookingButtonColorEnum.LIGHT_GREEN;
    }

    const action = this.getBookingActionFromBookingConfiguration(bookingConfigurationData);

    switch (bookingConfigurationData.bookingSettings.bookingType.name) {
      case BookingTypeEnum.INTERNAL:
        return BookingButtonColorEnum.GREEN;
      case BookingTypeEnum.ERRANDSYSTEM:
        return BookingButtonColorEnum.GREEN;
      case BookingTypeEnum.EXTERNAL:
        if (action === BookingActionEnum.URL) {
          if (this.isBookingUrlTracked(bookingConfigurationData?.bookingSettings)) {
            return BookingButtonColorEnum.GREEN; //BookingButtonColorEnum.LIGHT_GREEN;
          } else {
            return BookingButtonColorEnum.GREEN;
          }
        } else {
          return BookingButtonColorEnum.GREEN; //BookingButtonColorEnum.LIGHT_GREEN;
        }
      case BookingTypeEnum.EMAIL:
        return BookingButtonColorEnum.GREEN; //BookingButtonColorEnum.LIGHT_GREEN;
      case BookingTypeEnum.TEL:
        return BookingButtonColorEnum.GREEN; //BookingButtonColorEnum.LIGHT_GREEN;
      case BookingTypeEnum.HOMEPAGE:
        return BookingButtonColorEnum.GREEN; //BookingButtonColorEnum.LIGHT_GREEN;
      case BookingTypeEnum.VARDEN:
        return BookingButtonColorEnum.GREEN;
      default:
        return BookingButtonColorEnum.GREEN; //BookingButtonColorEnum.LIGHT_GREEN;
    }
  }

  public getBookingBtnLabelV2(bookingConfigurationData: BookingConfigurationData, isDisplayedAlone: boolean): string {
    if (
      !bookingConfigurationData ||
      !bookingConfigurationData.bookingSettings ||
      !bookingConfigurationData.bookingSettings.bookingType
    ) {
      return 'Boka nu';
    }

    switch (bookingConfigurationData.bookingSettings.bookingType.name) {
      case BookingTypeEnum.INTERNAL:
        if (bookingConfigurationData.careserviceId && isDisplayedAlone !== true) {
          return 'Fler tider';
        } else {
          return 'Boka tid nu';
        }
      case BookingTypeEnum.ERRANDSYSTEM:
        return 'Starta ärende';
      case BookingTypeEnum.EXTERNAL:
        return 'Boka tid';
      case BookingTypeEnum.EMAIL:
        return 'Bokningsförfrågan';
      case BookingTypeEnum.TEL:
        return 'Ta kontakt';
      case BookingTypeEnum.HOMEPAGE:
        return 'Till hemsida';
      case BookingTypeEnum.VARDEN:
        if (bookingConfigurationData.careserviceId && isDisplayedAlone !== true) {
          return 'Fler tider';
        } else {
          return 'Boka tid nu';
        }
      default:
        return 'Boka tid';
    }
  }

  // public isListaDigEnabled(bookingConfigurationData: BookingConfigurationData): boolean {
  //   return (
  //     !this.tools.isNil(bookingConfigurationData) &&
  //     !!bookingConfigurationData.allCustomerFeatures.find((f) => f.type === CustomerFeatureTypeEnum.LISTA_DIG_DIRECT)
  //   );
  // }

  public getEventText(bookingConfigurationData: BookingConfigurationData): string {
    let eventText = '';
    if (this.isBookingDefined(bookingConfigurationData)) {
      eventText = `${bookingConfigurationData.name}|${bookingConfigurationData.id}`;
      if (!this.tools.isNil(bookingConfigurationData.careserviceId)) {
        eventText += `|${bookingConfigurationData.careserviceName}|${bookingConfigurationData.careserviceId}`;
      }
    }
    return eventText;
  }

  public registerClickEventAndRedirectToBookingUrl(
    bookingConfigurationData: BookingConfigurationData,
    pageType: DLPageTypeEnum,
  ): void {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: 'bokning',
      Action: 'extern bokning',
      Label: this.getEventText(bookingConfigurationData),
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `bokning - extern bokning`,
      cpruId: bookingConfigurationData.id,
      careServiceId: bookingConfigurationData.careserviceId,
      careServiceTypeId: bookingConfigurationData.careserviceTypeId,
      inferredCategoryTypeId: bookingConfigurationData.mainCaregiverUnitTypeId,
      inferredCareProviderTypeAreaId: bookingConfigurationData.areaId,
      mainCareProviderTypeId: bookingConfigurationData.mainCaregiverUnitTypeId,
      pageType: pageType,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);

    const redirectUrl: string = this.getBookingUrl(bookingConfigurationData);
    this.document.location.href = redirectUrl;
  }

  public setBookingRouterState(state: BookingRouterState): void {
    this.bookingRouterStateWriteable.next(state);
  }

  public setBookingStepUrl(step?: BookingStepsEnum): void {
    const urlTree = this.router.parseUrl(this.router.url);
    const routerLink: string[] = [];
    const childrenKey = 'primary';
    const segments = urlTree.root.children[childrenKey].segments;
    segments.forEach((segment) => routerLink.push(segment.path));
    const queryParams = urlTree.queryParams;
    if (step != null) {
      queryParams[RoutesQueryParamsEnum.BOOKING_STEP] = step;
    } else {
      delete queryParams[RoutesQueryParamsEnum.BOOKING_STEP];
    }
    this.router.navigate(routerLink, {queryParams});
  }

  // TODO: refactor this when backend will provide better data structure
  public isBookingUrlTracked(bookingSettings: BookingSettings): boolean {
    return (
      bookingSettings &&
      (bookingSettings.isUrlTracked === true ||
        (bookingSettings.bookingType.name === BookingTypeEnum.EXTERNAL &&
          !this.tools.isNil(bookingSettings.bookingUrl) &&
          (bookingSettings.bookingUrl.indexOf('_vrd=') >= 0 || bookingSettings.bookingUrl.indexOf('bookingref=') >= 0)))
    );
  }

  private hasCustomerFeatureConfiguration(bookingConfigurationData: BookingConfigurationData): boolean {
    return (
      !this.tools.isNil(bookingConfigurationData) &&
      !this.tools.isNil(bookingConfigurationData.customerFeature) &&
      !this.tools.isNil(bookingConfigurationData.customerFeature.label) &&
      bookingConfigurationData.customerFeature.label !== '' &&
      !this.tools.isNil(bookingConfigurationData.customerFeature.bookingModalContent) &&
      bookingConfigurationData.customerFeature.bookingModalContent !== ''
    );
  }

  private hasStaticUrlConfiguration(bookingConfigurationData: BookingConfigurationData): boolean {
    return (
      this.isBookingDefined(bookingConfigurationData) &&
      !this.tools.isNil(bookingConfigurationData.bookingSettings.bookingType) &&
      (bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.INTERNAL ||
        bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.EXTERNAL ||
        bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.VARDEN ||
        bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.HOMEPAGE ||
        bookingConfigurationData.bookingSettings.bookingType.name === BookingTypeEnum.ERRANDSYSTEM)
    );
  }
}
